import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import { NextIntlClientProvider } from "next-intl";
import theme from "@/theme";
import { QuestionnaireLayout } from "@/components/QuestionnaireLayout/QuestionnaireLayout";
import { AnswerValue } from "@/commonTypes/answer";
import { AssessmentProvider } from "@/contexts/AssessmentContext/AssessmentContext";
import { ApiProvider } from "@/contexts/ApiContext/ApiContext";
import {
  SessionProvider,
  useSessionId,
} from "@/contexts/SessionContext/SessionContext";
import { FriendlyCaptchaProvider } from "@/contexts/FriendlyCaptchaContext/FriendlyCaptchaContext";
import { SessionManager } from "@/components/SessionManager/SessionManager";
import { mockApiService } from "@/api/mockApiService";
import type { ApiService } from "@/api/types";
import { apiService } from "@/api/apiService";
import { getAssetUrl } from "@/utils/assetUrl";
import {
  isKeyExpired,
  sessionItems,
  sessionStorageExpiring,
} from "@/utils/sessionStorage";
import {
  MARKETING_CONSENT_KEY,
  enforceMarketingConsentTrue,
} from "@/utils/marketingConsent";
import enUS from "@/locales/en-us.json";
// Import SCSS styles - esbuild will compile and bundle these
import "@/app/index.scss";
import {
  getSessionUtmsPayload,
  saveUtmQueryStringToSessionStorage,
} from "@/utils/utms";

// Inject @font-face dynamically to use the correct asset base URL
function injectFontFace(): void {
  if (document.getElementById("sap-vastago-font")) return;
  const style = document.createElement("style");
  style.id = "sap-vastago-font";
  style.textContent = `
    @font-face {
      font-family: "VastagoGrotesk";
      src: url("${getAssetUrl("/fonts/vastago-grotesk.otf")}") format("opentype");
      font-weight: normal;
      font-style: normal;
    }
  `;
  document.head.appendChild(style);
}

interface SleepAssessmentConfig {
  targetId: string;
  sessionId?: string;
  cmsRegion?: string;
  locale?: string;
  friendlyCaptchaSolution?: string;
  onComplete?: (answers: Record<string, AnswerValue>) => void;
  sessionExpiredRedirectUrl?: string;
}

const messages = {
  "en-us": enUS,
};

/*
 * TOU (Terms of Use) expiry handling.
 *
 * The landing page passes TOU acceptance via sessionStorage. We store our own
 * sap_termsOfUse key so it gets activity-based refresh with all other sap_ keys.
 */
const EXTERNAL_TERMS_KEY = "sleep-assessment-pro-terms-of-use";
const EXTERNAL_FRIENDLY_CAPTCHA_SOLUTION_KEY =
  "sleep-assessment-pro-friendly-captcha-solution";

function hasAcceptedTerms(): boolean {
  if (sessionStorage.getItem(EXTERNAL_TERMS_KEY) !== "true") return false;

  if (isKeyExpired(sessionItems.SESSION_ID)) {
    sessionStorageExpiring.clear();
    clearTermsAcceptance();
    return false;
  }

  if (isKeyExpired(sessionItems.TERMS_OF_USE)) {
    sessionStorageExpiring.clear();
    clearTermsAcceptance();
    return false;
  }

  return true;
}

function stampTermsAcceptance(): void {
  if (sessionStorageExpiring.get(sessionItems.TERMS_OF_USE)) return;
  sessionStorageExpiring.set(sessionItems.TERMS_OF_USE, true);
}

function clearTermsAcceptance(): void {
  sessionStorage.removeItem(EXTERNAL_TERMS_KEY);
  sessionStorageExpiring.remove(sessionItems.TERMS_OF_USE);
}

function resolveFriendlyCaptchaSolution(configSolution?: string): string | undefined {
  const trimmedConfig = configSolution?.trim();
  const externalSolution = sessionStorage
    .getItem(EXTERNAL_FRIENDLY_CAPTCHA_SOLUTION_KEY)
    ?.trim();
  const persistedSolution = sessionStorageExpiring
    .get<string>(sessionItems.FRIENDLY_CAPTCHA_SOLUTION)
    ?.trim();

  const solution = trimmedConfig || externalSolution || persistedSolution;
  if (!solution) return undefined;

  // Mirror external/config values into sap_ storage so expiry behavior is
  // consistent with other widget-managed session keys.
  sessionStorageExpiring.set(sessionItems.FRIENDLY_CAPTCHA_SOLUTION, solution);
  return solution;
}

function clearFriendlyCaptchaSolution(): void {
  sessionStorage.removeItem(EXTERNAL_FRIENDLY_CAPTCHA_SOLUTION_KEY);
  sessionStorageExpiring.remove(sessionItems.FRIENDLY_CAPTCHA_SOLUTION);
}

function redirectForMissingTerms(sessionExpiredRedirectUrl?: string): void {
  if (sessionExpiredRedirectUrl) {
    window.location.href = sessionExpiredRedirectUrl;
  }
}

/** Wrapper that uses sessionKey as a React key to force full remount on restart */
function WidgetContent({
  api,
  onComplete,
}: {
  api: ApiService;
  onComplete?: (answers: Record<string, AnswerValue>) => void;
}) {
  const { sessionKey } = useSessionId();
  return (
    <AssessmentProvider key={sessionKey} api={api} onComplete={onComplete}>
      <QuestionnaireLayout />
    </AssessmentProvider>
  );
}

// Widget initialization function
export function init(config: SleepAssessmentConfig): void {
  injectFontFace();

  const container = document.getElementById(config.targetId);
  if (!container) {
    console.error(
      `Sleep Assessment: Element with id "${config.targetId}" not found`,
    );
    return;
  }

  // Read data attributes as fallbacks; explicit config properties take priority
  const cmsRegion = config.cmsRegion ?? container.dataset.cmsRegion;
  const locale = config.locale ?? container.dataset.locale ?? "en-us";
  const sessionExpiredRedirectUrl =
    config.sessionExpiredRedirectUrl ??
    container.dataset.sessionExpiredRedirectUrl;

  if (cmsRegion) {
    console.info(`Sleep Assessment: cms-region=${cmsRegion}`);
  }

  if (!hasAcceptedTerms()) {
    container.innerHTML = "";
    console.info(
      "Sleep Assessment: widget not loaded — terms-of-use flag is not set to true",
    );
    redirectForMissingTerms(sessionExpiredRedirectUrl);
    return;
  }

  // Enforce policy: all widget sessions use marketing consent = true.
  enforceMarketingConsentTrue();
  stampTermsAcceptance();

  // Poll every 3 seconds to detect session expiry during user inactivity.
  // The read-triggered expiry only fires on interaction — polling ensures
  // idle users are redirected promptly without waiting for their next click.
  const expiryPollId = window.setInterval(() => {
    if (isKeyExpired(sessionItems.SESSION_ID)) {
      window.clearInterval(expiryPollId);
      window.dispatchEvent(
        new CustomEvent("sessionKeyExpired", {
          detail: { key: sessionItems.SESSION_ID },
        }),
      );
    }
  }, 3000);

  // Single unified expiry handler: any sap_ key expiry wipes all state and redirects to the landing page.
  window.addEventListener(
    "sessionKeyExpired",
    () => {
      window.clearInterval(expiryPollId);
      sessionStorageExpiring.clear();
      clearTermsAcceptance();
      clearFriendlyCaptchaSolution();
      sessionStorage.removeItem(MARKETING_CONSENT_KEY);
      window.location.href = sessionExpiredRedirectUrl ?? "/";
    },
    { once: true },
  );

  saveUtmQueryStringToSessionStorage();

  const useMockApi = process.env.NEXT_PUBLIC_USE_MOCK_API === "true";
  const api = useMockApi ? mockApiService : apiService;
  const isFriendlyCaptchaEnabled =
    process.env.NEXT_PUBLIC_FRIENDLY_CAPTCHA_ENABLED === "true";
  const captchaSolution = resolveFriendlyCaptchaSolution(
    config.friendlyCaptchaSolution,
  );

  const utmsPayload = getSessionUtmsPayload();
  const utmSessionId =
    config.sessionId ??
    sessionStorageExpiring.get<string>(sessionItems.SESSION_ID);
  if (utmSessionId && utmsPayload) {
    api.postUtms({ sessionId: utmSessionId, ...utmsPayload }).catch(() => {});
  }

  const root = createRoot(container);
  root.render(
    <ThemeProvider theme={theme}>
      <NextIntlClientProvider
        locale={locale}
        messages={
          messages[locale as keyof typeof messages] ?? messages["en-us"]
        }
        timeZone="UTC"
      >
        <div className="sap" data-dd-privacy="allow">
          <ApiProvider service={api}>
            <FriendlyCaptchaProvider
              enabled={isFriendlyCaptchaEnabled}
              solution={captchaSolution}
            >
              <SessionProvider
                externalSessionId={config.sessionId}
                sessionExpiredRedirectUrl={sessionExpiredRedirectUrl}
              >
                <SessionManager />
                <WidgetContent api={api} onComplete={config.onComplete} />
              </SessionProvider>
            </FriendlyCaptchaProvider>
          </ApiProvider>
        </div>
      </NextIntlClientProvider>
    </ThemeProvider>,
  );
}

// Auto-init if data attribute is present
if (typeof window !== "undefined") {
  // Auto-initialize on DOMContentLoaded if target element exists
  const autoInit = () => {
    const autoTarget = document.querySelector<HTMLElement>(
      "[data-sleep-assessment]",
    );
    if (autoTarget && autoTarget.id) {
      init({
        targetId: autoTarget.id,
        cmsRegion: autoTarget.dataset.cmsRegion,
        locale: autoTarget.dataset.locale,
        sessionExpiredRedirectUrl: autoTarget.dataset.sessionExpiredRedirectUrl,
      });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
}
